import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useSelector } from 'react-redux';
import { updateDatePicker } from '../redux/ProductionSlice';
interface Props {
  label?: string;
  name: string;
  formProps?: any;
  minDate?: any;
  disabled?: boolean;
  error?: boolean;
  helperText?: any;
  requiredField?: boolean | any;
  maxDate?: any;
  disablePast?: boolean;
  disableFuture?: boolean;
  defaultValue?: any;
  onChange?: any;
  value?: any;
  showTime?: any;
  highLighted?: boolean;
  placeholder?: string;
  control?: any;
  readOnly?: boolean;
  onBlur?: any;
  format?: any;
  formatDate?: boolean;
  validate?: boolean;
  id?: any;
}

const DateInput: React.FC<Props> = props => {
  const {
    label,
    name,
    formProps,
    minDate,
    disabled,
    error,
    helperText,
    requiredField,
    maxDate,
    disablePast,
    disableFuture,
    defaultValue = undefined,
    onChange,
    value,
    showTime: _showTime,
    highLighted: _highLighted,
    placeholder,
    control,
    readOnly: _readOnly,
    onBlur: _onBlur,
    format,
    formatDate,
    validate,
    id,
  } = props;

  const { datePickerStatus } = useSelector((state: any) => state?.production);

  const inputId: any = name; // each DateInput should have a unique name
  const isOpen = datePickerStatus === inputId;

  // updateDatePicker
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleClose = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };
  // const [open, setOpen] = useState(false)

  // Stops Enter from bubbling to the form's preventSubmitOnEnter guard, which
  // would otherwise cancel the native Enter-activation of the picker's buttons
  // (calendar icon, day cells, month/year switcher) even though they're portaled.
  const stopEnterPropagation = (event: any) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
    }
  };

  return (formProps && formProps?.control) || control ? (
    <Controller
      control={formProps?.control}
      defaultValue={defaultValue}
      name={name}
      render={({ field }) => (
        <FormControl
          fullWidth
          // className={`customSelect dateInput ${error ? 'border-red' : ''} ${
          //   highLighted ? 'highLighted' : ''
          // }`}
          error={error ? true : false}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FormLabel className="formLabel">
              {label} {requiredField ? <sup>*</sup> : null}{' '}
            </FormLabel>
            {formatDate && !validate ? (
              <DatePicker
                className="dateInputField"
                {...field}
                disabled={disabled}
                open={isOpen}
                onOpen={() => !disabled && updateDatePicker(inputId)}
                onClose={() => {
                  updateDatePicker(null);

                  handleClose();
                }}
                value={
                  field.value
                    ? dayjs(field.value)
                    : defaultValue
                    ? dayjs(defaultValue)
                    : null
                }
                views={['year', 'day']}
                showDaysOutsideCurrentMonth={false}
                minDate={minDate ? dayjs(minDate) : ''}
                defaultValue={defaultValue ? dayjs(defaultValue) : undefined}
                maxDate={maxDate ? dayjs(maxDate) : ''}
                slots={{
                  openPickerIcon: CalendarMonthOutlinedIcon,
                }}
                disableOpenPicker={false}
                disablePast={disablePast}
                disableFuture={disableFuture}
                slotProps={{
                  textField: {
                    size: 'small',
                    id: id || '',
                    error: error || false,
                    readOnly: true, // Disable manual entry
                    onClick: (event: any) => {
                      if (!disabled) {
                        event.stopPropagation(); // Prevent bubbling
                        updateDatePicker(inputId);
                      }
                    },
                    onKeyDown: (event: any) => {
                      if (event.key === 'Enter' && !disabled) {
                        event.stopPropagation(); // Skip the form's preventSubmitOnEnter guard
                        updateDatePicker(inputId);
                      }
                    },
                    disabled,

                    onBlur: field.onBlur,
                    ...(placeholder && { placeholder: placeholder }),
                  },
                  openPickerButton: {
                    onKeyDown: stopEnterPropagation,
                  },
                  popper: {
                    onKeyDown: stopEnterPropagation,
                  },
                }}
                sx={{
                  width: '100%',
                  input: {
                    padding: '8.5px 14px',
                  },
                }}
                onChange={(selectedDate: any) => {
                  if (selectedDate) {
                    const timestamp = new Date(selectedDate).getTime();
                    field.onChange(timestamp); // Update form value
                    if (onChange) onChange(selectedDate); // Call custom onChange if provided
                  }
                }}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onError={(_error, _value) => {}}
                format={field.value ? 'MMM DD, YYYY' : ''}
              />
            ) : validate ? (
              <DatePicker
                className="dateInputField"
                disabled={disabled}
                open={isOpen}
                onOpen={() => !disabled && updateDatePicker(inputId)}
                onClose={() => {
                  updateDatePicker(null);

                  handleClose();
                }}
                value={value ? dayjs(value) : null}
                views={['year', 'day']}
                showDaysOutsideCurrentMonth={false}
                minDate={minDate ? dayjs(minDate) : ''}
                defaultValue={defaultValue ? dayjs(defaultValue) : undefined}
                maxDate={maxDate ? dayjs(maxDate) : ''}
                slots={{
                  openPickerIcon: CalendarMonthOutlinedIcon,
                }}
                disableOpenPicker={false}
                disablePast={disablePast}
                disableFuture={disableFuture}
                slotProps={{
                  textField: {
                    size: 'small',
                    readOnly: true, // Disable manual entry
                    onClick: (event: any) => {
                      if (!disabled) {
                        event.stopPropagation(); // Prevent bubbling
                        updateDatePicker(inputId);
                      }
                    },
                    onKeyDown: (event: any) => {
                      if (event.key === 'Enter' && !disabled) {
                        event.stopPropagation(); // Skip the form's preventSubmitOnEnter guard
                        updateDatePicker(inputId);
                      }
                    },
                    inputRef: inputRef,
                    disabled,
                    onBlur: field.onBlur,
                    ...(placeholder && { placeholder: placeholder }),
                  },
                  openPickerButton: {
                    onKeyDown: stopEnterPropagation,
                  },
                  popper: {
                    onKeyDown: stopEnterPropagation,
                  },
                }}
                sx={{
                  width: '100%',
                  input: {
                    padding: '8.5px 14px',
                  },
                }}
                onChange={(selectedDate: any) => {
                  if (selectedDate) {
                    if (onChange) onChange(selectedDate); // Call custom onChange if provided
                  }
                }}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onError={(_error, _value) => {}}
                format={field.value ? 'MMM DD, YYYY' : ''}
              />
            ) : (
              <DatePicker
                className="dateInputField"
                {...field}
                disabled={disabled}
                open={isOpen}
                onOpen={() => !disabled && updateDatePicker(inputId)}
                onClose={() => updateDatePicker(null)}
                value={
                  field.value
                    ? dayjs(field.value)
                    : defaultValue
                    ? dayjs(defaultValue)
                    : null
                }
                views={['year', 'day']}
                showDaysOutsideCurrentMonth={false}
                minDate={minDate ? dayjs(minDate) : ''}
                defaultValue={defaultValue ? dayjs(defaultValue) : undefined}
                maxDate={maxDate ? dayjs(maxDate) : ''}
                slots={{
                  openPickerIcon: CalendarMonthOutlinedIcon,
                }}
                disableOpenPicker={false}
                disablePast={disablePast}
                disableFuture={disableFuture}
                slotProps={{
                  textField: {
                    size: 'small',
                    readOnly: true, // Disable manual entry
                    onClick: (event: any) => {
                      if (!disabled) {
                        event.stopPropagation(); // Prevent bubbling
                        updateDatePicker(inputId);
                      }
                    },
                    onKeyDown: (event: any) => {
                      if (event.key === 'Enter' && !disabled) {
                        event.stopPropagation(); // Skip the form's preventSubmitOnEnter guard
                        updateDatePicker(inputId);
                      }
                    },
                    disabled,
                    onBlur: field.onBlur,
                    ...(placeholder && { placeholder: placeholder }),
                  },
                  openPickerButton: {
                    onKeyDown: stopEnterPropagation,
                  },
                  popper: {
                    onKeyDown: stopEnterPropagation,
                  },
                }}
                sx={{
                  width: '100%',
                  input: {
                    padding: '8.5px 14px',
                  },
                }}
                onChange={(selectedDate: any) => {
                  if (selectedDate) {
                    const timestamp = new Date(selectedDate).getTime();
                    field.onChange(timestamp); // Update form value
                    if (onChange) onChange(selectedDate); // Call custom onChange if provided
                  }
                }}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onError={(_error, _value) => {}}
                format={format}
              />
            )}
          </LocalizationProvider>
          <FormHelperText className={error ? 'Mui-error' : ''}>
            {helperText}
          </FormHelperText>
        </FormControl>
      )}
    />
  ) : (
    <FormControl
      fullWidth
      // className={`customSelect dateInput ${error ? 'border-red' : ''}`}
      error={!!error}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormLabel className="formLabel">
          {label} {requiredField ? <sup>*</sup> : null}{' '}
        </FormLabel>
        {formatDate ? (
          <DatePicker
            disabled={disabled}
            value={value ? dayjs(value) : null} // Ensure it starts empty
            onChange={onChange}
            views={['year', 'day']}
            showDaysOutsideCurrentMonth={false}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            disablePast={disablePast}
            disableFuture={disableFuture}
            open={isOpen}
            onOpen={() => !disabled && updateDatePicker(inputId)}
            onClose={() => updateDatePicker(inputId)}
            slots={{
              openPickerIcon: CalendarMonthOutlinedIcon,
            }}
            disableOpenPicker={false}
            slotProps={{
              textField: {
                size: 'small',
                readOnly: true, // Disable manual entry
                onClick: (event: any) => {
                  if (!disabled) {
                    event.stopPropagation(); // Prevent bubbling
                    setOpen(true);
                  }
                },
                // onBlur: onBlur,
                disabled,
              },
            }}
            sx={{
              width: '100%',
              input: {
                padding: '8.5px 14px',
              },
            }}
            format="MMM DD, YYYY"
          />
        ) : (
          <DatePicker
            disabled={disabled}
            value={value ? dayjs(value) : null} // Ensure it starts empty
            onChange={onChange}
            views={['year', 'day']}
            showDaysOutsideCurrentMonth={false}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            disablePast={disablePast}
            disableFuture={disableFuture}
            open={isOpen}
            onOpen={() => !disabled && updateDatePicker(inputId)}
            onClose={() => updateDatePicker(inputId)}
            slots={{
              openPickerIcon: CalendarMonthOutlinedIcon,
            }}
            disableOpenPicker={false}
            slotProps={{
              textField: {
                size: 'small',
                readOnly: true, // Disable manual entry
                onClick: (event: any) => {
                  if (!disabled) {
                    event.stopPropagation(); // Prevent bubbling
                    setOpen(true);
                  }
                },
                // onBlur: onBlur,
                disabled,
              },
            }}
            sx={{
              width: '100%',
              input: {
                padding: '8.5px 14px',
              },
            }}
            format={format}
          />
        )}
      </LocalizationProvider>
      <FormHelperText className={error ? 'Mui-error' : ''}>
        {helperText}
      </FormHelperText>
    </FormControl>
  );
};

export default DateInput;
